import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import UserService from "../services/userService";

const defaultCreateUserState = {
  userCreateLoading: false,
  userCreateData: null,
  userCreateError: null,
};

const defaultGetUserState = {
  userGetLoading: false,
  userGetData: null,
  userGetError: null,
};

const defaultUpdateUserState = {
  userUpdateLoading: false,
  userUpdateData: null,
  userUpdateError: null,
};

const defaultChangePasswordState = {
  userChangePasswordLoading: false,
  userChangePasswordData: null,
  userChangePasswordError: null,
};

const defaultUserListState = {
  userUserListLoading: false,
  userUserListData: null,
  userUserListError: null,
};

const defaultUserPhotoListState = {
  userUserPhotoLoading: false,
  userUserPhotoData: null,
  userUserPhotoError: null,
};

const fetchCreateUserData = createAsyncThunk("user/create", async (userReq) => {
  return await UserService.createUser(userReq);
});

const fetchGetUserData = createAsyncThunk("user/get", async () => {
  return await UserService.getUser();
});

const fetchQuicksightEmbedUrl = createAsyncThunk("quicksight/embedurl", async () => {
  return await UserService.getQuicksightEmbedUrl();
});

const fetchUpdateUserData = createAsyncThunk("user/update", async (userReq) => {
  return await UserService.updateUser(userReq);
});

const fetchChangePasswordData = createAsyncThunk(
  "user/changepassword",
  async (userReq) => {
    return await UserService.changePassword(userReq);
  },
);

const fetchUserListData = createAsyncThunk("user/filter", async (userReq) => {
  return await UserService.getUserList(userReq);
});

const fetchUserPhotoData = createAsyncThunk("user/photo", async (userReq) => {
  return await UserService.uploadPhoto(userReq);
});

const userCreateReducer = createSlice({
  name: "getUser",
  initialState: defaultCreateUserState,
  extraReducers: (builder) => {
    builder.addCase(fetchCreateUserData.pending, (state) => {
      state.userCreateLoading = true;
    });
    builder.addCase(fetchCreateUserData.fulfilled, (state, action) => {
      state.userCreateLoading = false;
      state.userCreateData = action.payload;
      state.userCreateError = "";
    });
    builder.addCase(fetchCreateUserData.rejected, (state, action) => {
      state.userCreateLoading = false;
      state.userCreateData = null;
      state.userCreateError = action.error.message;
    });
  },
});

const userGetReducer = createSlice({
  name: "getUser",
  initialState: defaultGetUserState,
  extraReducers: (builder) => {
    builder.addCase(fetchGetUserData.pending, (state) => {
      state.userGetLoading = true;
    });
    builder.addCase(fetchGetUserData.fulfilled, (state, action) => {
      state.userGetLoading = false;
      state.userGetData = action.payload;
      state.userGetError = "";
    });
    builder.addCase(fetchGetUserData.rejected, (state, action) => {
      state.userGetLoading = false;
      state.userGetData = null;
      state.userGetError = action.error.message;
    });
  },
});

const userUpdateReducer = createSlice({
  name: "updateUser",
  initialState: defaultUpdateUserState,
  extraReducers: (builder) => {
    builder.addCase(fetchUpdateUserData.pending, (state) => {
      state.userUpdateLoading = true;
    });
    builder.addCase(fetchUpdateUserData.fulfilled, (state, action) => {
      state.userUpdateLoading = false;
      state.userUpdateData = action.payload;
      state.userUpdateError = "";
    });
    builder.addCase(fetchUpdateUserData.rejected, (state, action) => {
      state.userUpdateLoading = false;
      state.userUpdateData = null;
      state.userUpdateError = action.error.message;
    });
  },
});

const changePasswordReducer = createSlice({
  name: "changePasswordUser",
  initialState: defaultChangePasswordState,
  extraReducers: (builder) => {
    builder.addCase(fetchChangePasswordData.pending, (state) => {
      state.userChangePasswordLoading = true;
    });
    builder.addCase(fetchChangePasswordData.fulfilled, (state, action) => {
      state.userChangePasswordLoading = false;
      state.userChangePasswordData = action.payload;
      state.userChangePasswordError = "";
    });
    builder.addCase(fetchChangePasswordData.rejected, (state, action) => {
      state.userChangePasswordLoading = false;
      state.userChangePasswordData = null;
      state.userChangePasswordError = action.error.message;
    });
  },
});

const userListReducer = createSlice({
  name: "getUserList",
  initialState: defaultUserListState,
  extraReducers: (builder) => {
    builder.addCase(fetchUserListData.pending, (state) => {
      state.userUserListLoading = true;
    });
    builder.addCase(fetchUserListData.fulfilled, (state, action) => {
      state.userUserListLoading = false;
      state.userUserListData = action.payload;
      state.userUserListError = "";
    });
    builder.addCase(fetchUserListData.rejected, (state, action) => {
      state.userUserListLoading = false;
      state.userUserListData = null;
      state.userUserListError = action.error.message;
    });
  },
});

const userPhotoReducer = createSlice({
  name: "userPhoto",
  initialState: defaultUserPhotoListState,
  extraReducers: (builder) => {
    builder.addCase(fetchUserPhotoData.pending, (state) => {
      state.userUserPhotoLoading = true;
    });
    builder.addCase(fetchUserPhotoData.fulfilled, (state, action) => {
      state.userUserPhotoLoading = false;
      state.userUserPhotoData = action.payload;
      state.userUserPhotoError = "";
    });
    builder.addCase(fetchUserPhotoData.rejected, (state, action) => {
      state.userUserPhotoLoading = false;
      state.userUserPhotoData = null;
      state.userUserPhotoError = action.error.message;
    });
  },
});

const getQuicksightEmbedUrl = createSlice({
  name: "getQuicksightEmbedUrl",
  initialState: defaultGetUserState,
  reducers: {
    resetQuicksightEmbedUrl: (state) => {
      state.quicksightData = null;
      state.getQuicksightEmbedUrlLoading = false;
      state.getQuicksightEmbedUrlError = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchQuicksightEmbedUrl.pending, (state) => {
      state.getQuicksightEmbedUrlLoading = true;
    });
    builder.addCase(fetchQuicksightEmbedUrl.fulfilled, (state, action) => {
      state.getQuicksightEmbedUrlLoading = false;
      state.quicksightData = action.payload;
      state.getQuicksightEmbedUrlError = "";
    });
    builder.addCase(fetchQuicksightEmbedUrl.rejected, (state, action) => {
      state.getQuicksightEmbedUrlLoading = false;
      state.quicksightData = null;
      state.getQuicksightEmbedUrlError = action.error.message;
    });
  },
});

export const UserCreateReducer = userCreateReducer.reducer;
export const UserGetReducer = userGetReducer.reducer;
export const UserUpdateReducer = userUpdateReducer.reducer;
export const ChangePasswordReducer = changePasswordReducer.reducer;
export const UserListReducer = userListReducer.reducer;
export const UserPhotoReducer = userPhotoReducer.reducer;
export const GetQuicksightEmbedUrlReducer = getQuicksightEmbedUrl.reducer;


export const FetchCreateUserData = fetchCreateUserData;
export const FetchGetUserData = fetchGetUserData;
export const FetchUpdateUserData = fetchUpdateUserData;
export const FetchChangePasswordData = fetchChangePasswordData;
export const FetchUserListData = fetchUserListData;
export { fetchUserListData };
export const FetchUserPhotoData = fetchUserPhotoData;
export const FetchQuicksightEmbedUrl = fetchQuicksightEmbedUrl;
export const { resetQuicksightEmbedUrl } = getQuicksightEmbedUrl.actions;


